/**
 * adminDepartmentStore — Departments slice of the admin store.
 *
 * Endpoints:
 *   GET    /api/admin/departments
 *   POST   /api/admin/departments
 *   PATCH  /api/admin/departments/:id
 *   DELETE /api/admin/departments/:id
 */
import { API_URL } from "../../constants";
import { toDisplayString } from "../../utils/toDisplayString";
import { authHeaders, resolveToken } from "./adminUtils";

export const departmentInitialState = {
  departments: [],
  departmentsLoading: false,
  departmentsError: null,
  departmentMutationLoading: false,
  departmentMutationError: null,
};

async function apiRequest(get, path, { method = "GET", body } = {}) {
  const response = await fetch(`${API_URL}${path}`, {
    method,
    headers: authHeaders(resolveToken(get)),
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text();
  let data = {};
  if (text) {
    const contentType = response.headers.get("content-type") || "";
    const trimmed = text.trim();
    if (contentType.includes("application/json") || trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        data = JSON.parse(text);
      } catch {
        data = {};
      }
    }
  }

  if (!response.ok || (data && data.error)) {
    throw new Error(
      toDisplayString(data?.error ?? data?.message, `Request failed (${response.status})`)
    );
  }

  return data;
}

function normalizeDepartments(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return data?.items || data?.departments || [];
}

export function createDepartmentActions(set, get) {
  async function mutate(path, method, body, successMessage) {
    set({ departmentMutationLoading: true, departmentMutationError: null });
    try {
      const data = await apiRequest(get, path, { method, body });
      set({
        departmentMutationLoading: false,
        departmentMutationError: null,
        successMessage,
      });
      return { success: true, data: data?.data ?? data };
    } catch (err) {
      const message = toDisplayString(err.message, "Action failed");
      set({ departmentMutationLoading: false, departmentMutationError: message });
      return { success: false, error: message };
    }
  }

  return {
    fetchDepartments: async () => {
      set({ departmentsLoading: true, departmentsError: null });
      try {
        const data = await apiRequest(get, "/api/admin/departments");
        const departments = normalizeDepartments(data);
        set({ departmentsLoading: false, departments });
        return { success: true, departments };
      } catch (err) {
        const message = toDisplayString(err.message, "Failed to fetch departments");
        set({ departmentsLoading: false, departmentsError: message });
        return { success: false, error: message };
      }
    },

    createDepartment: (payload) =>
      mutate("/api/admin/departments", "POST", payload, "Department created"),

    updateDepartment: (id, payload) =>
      mutate(`/api/admin/departments/${id}`, "PATCH", payload, "Department updated"),

    deleteDepartment: (id) =>
      mutate(`/api/admin/departments/${id}`, "DELETE", undefined, "Department deleted"),
  };
}

const departmentActions = createDepartmentActions;
export default departmentActions;
